import Vue from 'vue'
import VueTailwind from 'vue-tailwind'

import tailwindSettings from "./components/VueTailwindSettings";
import NormalizedOptions from 'vue-tailwind/dist/types/NormalizedOptions'

import axios from 'axios'
import { getTimeZones, TimeZone } from '@vvo/tzdb'
import SearchPagination from './components/SearchPagination.vue'
import ClassSearch from './components/ClassSearch.vue'

import { CountryDto, LanguageDto } from "./models/PublicApi";

// Use shared settings, see VueTailwindSettings.ts for more details.
Vue.use(VueTailwind, tailwindSettings);

// This has to be registered globally because it is consumed by the vue-good-table
Vue.component('class-search-pagination', SearchPagination);

// Registering globally also removes intellisense warnings about unknown tags
// TODO: Fix vue-good-table by referencing local packages at some point.
Vue.component('class-search', ClassSearch)

interface SearchParams {
    certificationAbbreviation: string,
    deliveryMethod: string,
    timeZone: string,
    startTime: string,
    // regionId: number | null,
    countryId: number | null,
    languageCode: string | null
}

export function initFindAClassVue(element: string, countries: CountryDto[], languages: LanguageDto[]) {
    if (!document.getElementById(element)) return;

    const vm = new Vue({
        el: '#' + element,
        data() {
            return {
                hasRunASearch: false,
                allCountries: countries,
                allLanguages: languages,
                timezones: [] as NormalizedOptions,
                countries: [] as NormalizedOptions,
                languages: [] as NormalizedOptions,
                isLoading: false,
                searchResults: [] as any[], // Reactive array for search results
                searchParams: {
                    certificationAbbreviation: '',
                    deliveryMethod: '',
                    timeZone: '',
                    startTime: '',
                    countryId: null,
                    languageCode: null,
                },
                pagination: {
                    currentPage: 1,
                    perPage: 6, // Number of cards per page
                },
                currentOffset: 0, // Offset for pagination
                hasMoreData: true, // Flag to check if more data is available
            };
        },
        computed: {
            totalPages() {
                if (!this.searchResults || this.searchResults.length === 0) {
                    return 1; // Default to 1 page if there are no results
                }
                return Math.ceil(this.searchResults.length / this.pagination.perPage);
            },
            paginatedRows() {
                if (!this.searchResults || this.searchResults.length === 0) {
                    return []; // Return an empty array if searchResults is null or empty
                }
                const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
                const end = start + this.pagination.perPage;
                return this.searchResults.slice(start, end);
            },
        },
        mounted() {
            const tzs = getTimeZones();
            const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.timezones = tzs.map(tz => {
                const display = this.getTimezoneDisplay(tz);
                if (currentTz === tz.name || tz.group.includes(currentTz)) {
                    this.searchParams.timeZone = tz.name; // Ensure searchParams is initialized
                }
                return {
                    text: display,
                    value: tz.name,
                };
            });
            this.updateCountryOptions(this.allCountries);
            this.updateLanguageOptions(this.allLanguages);
            this.updateSearchParamsFromUrl();
        },
        methods: {
            getProperty: function <T, K extends keyof T>(obj: T, key: K) {
                return obj[key];
            },
            getTimezoneDisplay: function (tz: TimeZone) {
                const hours = Math.floor(Math.abs(tz.currentTimeOffsetInMinutes / 60));
                const minutes = Math.abs(tz.currentTimeOffsetInMinutes % 60);
                const sign = tz.currentTimeOffsetInMinutes < 0 ? '-' : '+';
                const utcOffsetDisplay = (hours === 0 && minutes === 0)
                                         ? '(UTC)'
                                         : `(UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
                const topCities = tz.mainCities.slice(0, 3).join(', ');

                return `${utcOffsetDisplay} ${topCities}`;
            },
            // onRegionSelected: function (newRegionValue: string) {
            //     if (!newRegionValue) {
            //         // Reset country combo back to include all countries
            //         this.updateCountryOptions(this.allCountries);
            //     } else {
            //         const newList = this.allCountries.filter(country => country?.region?.id === parseInt(newRegionValue));
            //         this.updateCountryOptions(newList);
            //     }
            // },
            runSearch: async function (resetResults = false) {
                if (resetResults) {
                    this.searchResults = []; // Clear previous results
                    this.currentOffset = 0; // Reset offset
                    this.hasMoreData = true; // Reset the flag for more data
                    // Reset infinite scroll state in ClassSearch child
                    this.$nextTick(() => {
                        if (this.$refs.classSearch && typeof this.$refs.classSearch.resetInfiniteScrollState === 'function') {
                            this.$refs.classSearch.resetInfiniteScrollState();
                        }
                    });
                }
                // Always force a new object reference for reactivity
                this.searchParams = { ...this.searchParams };

                if (this.isLoading || !this.hasMoreData) return;

                // Update the URL with the current search parameters before starting the search
                this.updateUrlFromSearchParams();

                this.isLoading = true;
                this.hasRunASearch = true;
                try {
                    const response = await axios.get('/api/classes/search', {
                        params: {
                            ...this.searchParams,
                            offset: this.currentOffset,
                            limit: 15
                        }
                    });

                    this.searchResults = [...this.searchResults, ...response.data]; // Append new results

                    // Check if more data is available
                    if (response.data.length < 15) {
                        this.hasMoreData = false;
                    } else {
                        this.currentOffset += 15; // Increment offset for next batch
                    }
                } catch (error) {
                    console.error("Error running class search", error);
                } finally {
                    this.isLoading = false;
                }
            },
            onRowClick(row: ClassDto) {
                window.open('/class/' + row.id, '_blank');
            },
            updateCountryOptions: function(countries: CountryDto[]) {
                let selectedCountryFound = false;
                const newOptions = countries.map(country => {
                    if (this.searchParams.countryId === country.id) {
                        selectedCountryFound = true;
                    }
                    return {
                        text: country.name,
                        value: country.id
                    }
                });

                if (!selectedCountryFound) {
                    this.searchParams.countryId = null;
                }
                this.countries = newOptions;
            },
            updateLanguageOptions: function(languages: LanguageDto[]){
                const browserLanguage = navigator.language.split('-')[0];               
                let selectedLanguageFound = false;
                const newOptions = languages.map(language => {
                    if (browserLanguage  === language.code) {
                        selectedLanguageFound = true;
                    }
                    return {
                        text: language.name,
                        value: language.code
                    }
                });

                if (selectedLanguageFound) {
                    this.searchParams.languageCode = browserLanguage;                }
                else {
                    this.searchParams.languageCode = null;
                }
                this.languages = newOptions;
            },
            updateSearchParamsFromUrl: function() {
                const url = new URL(window.location.href);
                const sp = this.searchParams;
                let updatedParam = false;
                if (!!url.searchParams.get('cert') && url.searchParams.get('cert') != null) {
                    sp.certificationAbbreviation = <string>url.searchParams.get('cert');
                    updatedParam = true;
                }

                if (!!url.searchParams.get('delivery') && url.searchParams.get('delivery') != null) {
                    sp.deliveryMethod = <string>url.searchParams.get('delivery');
                    updatedParam = true;
                }
                // if (!!url.searchParams.get('region') && url.searchParams.get('region') != null) {
                //     sp.regionId = parseInt(<string>url.searchParams.get('region'))
                //     updatedParam = true;
                // }
                if (!!url.searchParams.get('tz') && url.searchParams.get('tz') != null) {
                    sp.timeZone = <string>url.searchParams.get('tz');
                    updatedParam = true;
                }
                if (!!url.searchParams.get('start') && url.searchParams.get('start') != null) {
                    sp.startTime = <string>url.searchParams.get('start');
                    updatedParam = true;
                }
                if (!!url.searchParams.get('language') && url.searchParams.get('language') != null) {
                    sp.languageCode = <string>url.searchParams.get('language');
                    updatedParam = true;
                }
                // Do country last due to region potentially causing issues.
                if (!!url.searchParams.get('country') && url.searchParams.get('country') != null) {
                    sp.countryId = parseInt(<string>url.searchParams.get('country'));
                    updatedParam = true;
                }

                if (updatedParam) {
                    this.runSearch();
                }
            },
            updateUrlFromSearchParams: function() {
                const url = new URL(window.location.href);
                const sp = this.searchParams;
                url.search = "";

                let keysToDelete: string[] = [];
                url.searchParams.forEach((value, key) => {
                    keysToDelete.push(key);
                });
                keysToDelete.forEach(k => {
                    url.searchParams.delete(k);
                })

                if (!!sp.certificationAbbreviation && sp.certificationAbbreviation.trim().length > 0) {
                    url.searchParams.set('cert', sp.certificationAbbreviation);
                }
                if (!!sp.countryId && sp.countryId >= 0) {
                    url.searchParams.set('country', sp.countryId.toString());
                }
                if (!!sp.languageCode && sp.languageCode.trim().length > 0) {
                    url.searchParams.set('language', sp.languageCode);
                }
                if (!!sp.deliveryMethod && sp.deliveryMethod.trim().length > 0) {
                    url.searchParams.set('delivery', sp.deliveryMethod);
                }
                // if (!!sp.regionId && sp.regionId >= 0) {
                //     url.searchParams.set('region', sp.regionId.toString());
                // }
                if (!!sp.timeZone && sp.timeZone.trim().length > 0) {
                    url.searchParams.set('tz', sp.timeZone);
                }
                if (!!sp.startTime && sp.startTime.trim().length > 0) {
                    url.searchParams.set('start', sp.startTime);
                }

                // This doesn't get placed in the history, see: https://stackoverflow.com/questions/22753052/remove-url-parameters-without-refreshing-page
                window.history.replaceState(null, '', url.toString());
            }
        }
    });
}
